import Button, { ButtonProps } from '@mui/material/Button';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppContext from './AppContext';
import { AppI18NLanguageInfo } from '@/app/i18n/types';

export interface LocaleSwitcherProps {
  size?: ButtonProps['size'];
  sx?: ButtonProps['sx'];
}

export default function LocaleSwitcher({ size, sx }: LocaleSwitcherProps) {
  const app = useContext(AppContext);
  const { t, i18n } = useTranslation(app.i18n.lng);
  const [languages, setLanguages] = useState<AppI18NLanguageInfo[]>(
    app.i18n.languages
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  /**
   * This is to allow dynamic fetching of on-line languages from Locize,
   * when Locize backend is used.
   */
  useEffect(() => {
    const connector = i18n.services.backendConnector?.backend;
    if (connector == undefined || typeof connector.getLanguages !== 'function')
      return;

    let isMounted = true;

    connector.getLanguages((err: any, ret: any) => {
      if (err) return;

      setLanguages(
        Object.entries(ret).map(([code, info]: any) => ({ code, ...info }))
      );
    });

    return () => {
      isMounted = false;
    };
  }, [i18n.services.backendConnector?.backend]);

  return (
    <>
      <Button
        id="locale-switcher-button"
        aria-controls={open ? 'locale-switcher-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disableElevation
        size={size}
        variant="outlined"
        sx={{
          borderRadius: 2,
          color: 'text.secondary',
          backgroundColor: 'transparent',
          borderColor: 'divider',
          minWidth: 0,
          width: 'auto',
          height: 'auto',
          ...sx,
        }}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          setAnchorEl(event.currentTarget);
        }}
      >
        {app.i18n.lng}
      </Button>
      <Menu
        id="locale-switcher-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          setAnchorEl(null);
        }}
        slotProps={{
          list: {
            'aria-labelledby': 'locale-switcher-button',
          },
        }}
      >
        {languages.map((lng) => (
          <MenuItem
            key={lng.code}
            dense
            selected={app.i18n.lng === lng.code}
            onClick={() => {
              i18n.changeLanguage(lng.code);
              app.i18n.setLang(lng.code);
              setAnchorEl(null);
            }}
          >
            <ListItemText>{t(lng.nativeName)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
